/**
 * React Query Hook: List User Branches
 *
 * Fetches the current user's branches for the selected organization.
 * Returns a flat array of MetadataBranch objects.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { USER_BRANCHES_QUERY } from '@/services/metadata_v1/queries';
import type {
  BranchStatus,
  MetadataBranch,
  UserBranchesResponse,
  UserBranchesVariables,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

async function fetchUserBranches(
  variables: UserBranchesVariables
): Promise<MetadataBranch[]> {
  const response = await graphqlRequest<UserBranchesResponse>(
    USER_BRANCHES_QUERY,
    variables
  );
  return response.userBranches.edges.map((e) => e.node);
}

/**
 * Hook to list the current user's branches for the selected organization.
 *
 * @param options.status   - Filter by status (ACTIVE | MERGED | ABANDONED)
 * @param options.enabled  - Whether the query should run
 *
 * @returns React Query result — `data` is a flat MetadataBranch[]
 */
export function useUserBranches(options?: {
  status?: BranchStatus;
  enabled?: boolean;
}): UseQueryResult<MetadataBranch[], Error> {
  const { status, enabled = true } = options || {};

  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const organizationId = selectedOrg?.id;

  return useQuery({
    queryKey: ['userBranches', organizationId, status],
    queryFn: () =>
      fetchUserBranches({
        organizationId: organizationId!,
        status,
      }),
    enabled: enabled && !!organizationId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
